import React, { useEffect, useState } from 'react';
import { useAppStore } from '../App';
import { FamilyAvatar } from './FamilyAvatar';

interface PeerSearchResult {
 moPeerHash: string;
 email: string;
 name: string;
 profilePicture?: string | null;
}

interface PresetRecipient {
 hash: string;
 name: string;
 email?: string;
}

interface Props {
 /** 수신자 고정 모드 — admin → 회원 메시지처럼 미리 지정. 없으면 inbox 의 '새 메시지' 처럼 검색 */
 presetRecipient?: PresetRecipient;
 onClose: () => void;
 /** 전송 성공 시 호출 — conversationId 로 navigate 가능 */
 onSent: (conversationId: string) => void;
}

const ComposeMessageModal: React.FC<Props> = ({ presetRecipient, onClose, onSent }) => {
 const { user } = useAppStore();
 const [recipient, setRecipient] = useState<PresetRecipient | null>(presetRecipient ?? null);
 const [query, setQuery] = useState('');
 const [searchResults, setSearchResults] = useState<PeerSearchResult[]>([]);
 const [searching, setSearching] = useState(false);
 const [subject, setSubject] = useState('');
 const [body, setBody] = useState('');
 const [sending, setSending] = useState(false);
 const [error, setError] = useState<string | null>(null);

 // 검색 — debounce 없이 단순 trigger (수신자 미지정 모드만)
 useEffect(() => {
  if (presetRecipient || !query || query.length < 2) {
   setSearchResults([]);
   return;
  }
  setSearching(true);
  fetch(`/mopeer/search?q=${encodeURIComponent(query)}&limit=10`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (j.success) {
     // 자기 자신 제외
     const filtered = (j.data as PeerSearchResult[]).filter(p =>
      p.moPeerHash !== user?.moPeerHash &&
      ((p.name?.includes(query)) || (p.email?.includes(query)))
     );
     setSearchResults(filtered);
    }
   })
   .catch(() => setSearchResults([]))
   .finally(() => setSearching(false));
 }, [query, presetRecipient, user?.moPeerHash]);

 const send = async () => {
  if (!recipient || !user) return;
  if (!body.trim()) {
   setError('메시지 내용을 입력하세요');
   return;
  }
  setSending(true);
  setError(null);
  try {
   const r = await fetch('/messages', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
     kind: 'direct',
     subject: subject.trim() || null,
     participants: [
      { peer_hash: user.moPeerHash, role: 'sender' },
      { peer_hash: recipient.hash, role: 'recipient' },
     ],
     first_message: {
      body: body.trim(),
      message_type: 'text',
      payload: {},
      attachments: [],
     },
    }),
   });
   const j = await r.json();
   if (!r.ok || !j.success) throw new Error(j.error || '메시지 전송 실패');
   onSent(j.data.conversation_id);
  } catch (err: any) {
   setError(err?.message || '메시지 전송 실패');
   setSending(false);
  }
 };

 return (
  <div
   className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
   onClick={() => !sending && onClose()}
  >
   <div className="bg-white max-w-lg w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
    <h3 className="text-lg font-semibold mb-3">새 메시지</h3>

    {/* 수신자 영역 */}
    {recipient ? (
     <div className="mb-3 flex items-center gap-2 text-sm">
      <span className="text-gray-500">받는 사람:</span>
      <strong>{recipient.name}</strong>
      {recipient.email && <span className="text-gray-400 text-xs">({recipient.email})</span>}
      {!presetRecipient && (
       <button
        type="button"
        onClick={() => { setRecipient(null); setQuery(''); }}
        className="ml-auto text-xs text-blue-600 hover:underline"
        disabled={sending}
       >변경</button>
      )}
     </div>
    ) : (
     <div className="mb-3">
      <label className="block text-xs text-gray-600 mb-1">받는 사람 — 이름·이메일 검색 (2자 이상)</label>
      <input
       type="text"
       value={query}
       onChange={(e) => setQuery(e.target.value)}
       placeholder="검색..."
       autoFocus
       className="w-full border border-gray-300 px-3 py-2 text-sm"
      />
      {searching && <div className="text-xs text-gray-400 mt-1">검색 중...</div>}
      {!searching && query.length >= 2 && searchResults.length === 0 && (
       <div className="text-xs text-gray-400 mt-1">결과 없음</div>
      )}
      {searchResults.length > 0 && (
       <ul className="border border-gray-200 mt-1 max-h-48 overflow-y-auto divide-y divide-gray-100">
        {searchResults.map(p => (
         <li
          key={p.moPeerHash}
          onClick={() => setRecipient({ hash: p.moPeerHash, name: p.name || p.email, email: p.email })}
          className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-gray-50 text-sm"
         >
          <FamilyAvatar
           name={p.name || p.email}
           familyHash={p.moPeerHash}
           imageUrl={p.profilePicture}
           size={24}
          />
          <span className="font-medium">{p.name || '(이름 없음)'}</span>
          <span className="text-xs text-gray-500">{p.email}</span>
         </li>
        ))}
       </ul>
      )}
     </div>
    )}

    {/* 제목·내용 */}
    <div className="space-y-3 text-sm">
     <div>
      <label className="block text-xs text-gray-600 mb-1">제목 (선택)</label>
      <input
       type="text"
       value={subject}
       onChange={(e) => setSubject(e.target.value)}
       maxLength={120}
       disabled={sending}
       placeholder="(없으면 빈 제목으로 전송)"
       className="w-full border border-gray-300 px-3 py-2 text-sm"
      />
     </div>
     <div>
      <label className="block text-xs text-gray-600 mb-1">내용</label>
      <textarea
       value={body}
       onChange={(e) => setBody(e.target.value)}
       disabled={sending}
       rows={6}
       className="w-full border border-gray-300 px-3 py-2 text-sm resize-y"
       placeholder="메시지 내용을 입력하세요"
      />
     </div>
    </div>

    {error && <div className="bg-red-50 text-red-700 p-2 text-xs mt-3">{error}</div>}

    <div className="flex justify-end gap-2 mt-5">
     <button
      type="button"
      onClick={onClose}
      disabled={sending}
      className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
     >취소</button>
     <button
      type="button"
      onClick={send}
      disabled={sending || !recipient || !body.trim()}
      className="px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
     >
      {sending ? '보내는 중...' : '보내기'}
     </button>
    </div>
   </div>
  </div>
 );
};

export default ComposeMessageModal;
